<template>
  <div class="rtab" :style="wide ? { minWidth: tableMin + 'px' } : null">
    <!-- header: title · count · columns · search -->
    <div class="rtab__head">
      <div class="rtab__title">{{ title }}</div>
      <div class="rtab__count">{{ rows.length }} de {{ totalCount }}</div>

      <div class="rtab__tools">
        <button
          v-if="wide"
          type="button"
          class="rtab__colsbtn"
          :class="{ 'rtab__colsbtn--on': colsOpen }"
          @click="colsOpen = !colsOpen"
        >
          <hero-icon :icon="ColumnsIcon" :size="15" />
          Columnas
          <span class="rtab__colscount">{{ visibleColumns.length }}/{{ columns.length }}</span>
        </button>

        <div class="rtab__searchwrap">
          <hero-icon :icon="SearchIcon" :size="15" class="rtab__searchicon" />
          <input
            :value="search"
            type="text"
            class="rtab__search"
            placeholder="Buscar…"
            @input="$emit('search', $event.target.value)"
          />
        </div>
      </div>

      <!-- column visibility + order -->
      <div v-if="colsOpen" ref="colspop" class="rtab__colspop">
        <div class="rtab__colshead">
          <span>Columnas visibles</span>
          <button type="button" class="rtab__colsreset" @click="resetCols">Restablecer</button>
        </div>
        <div v-for="(col, i) in orderedColumns" :key="col.key" class="rtab__colrow">
          <hero-icon :icon="BarsIcon" :size="14" class="rtab__colgrip" />
          <button type="button" class="rtab__coltoggle" @click="toggleCol(col.key)">
            <span class="rtab__colbox" :class="{ 'rtab__colbox--on': !hidden[col.key] }">
              <hero-icon v-if="!hidden[col.key]" :icon="CheckIcon" :size="11" />
            </span>
            <span class="rtab__collbl" :class="{ 'rtab__collbl--off': hidden[col.key] }">{{ col.label }}</span>
          </button>
          <button type="button" class="rtab__colmove" :disabled="i === 0" @click="moveCol(col.key, -1)">
            <hero-icon :icon="ArrowUpIcon" :size="15" />
          </button>
          <button
            type="button"
            class="rtab__colmove"
            :disabled="i === orderedColumns.length - 1"
            @click="moveCol(col.key, 1)"
          >
            <hero-icon :icon="ArrowDownIcon" :size="15" />
          </button>
        </div>
      </div>
    </div>

    <!-- wide: sortable header row -->
    <div v-if="wide" class="rtab__hrow" :style="{ gridTemplateColumns: grid }">
      <button
        v-for="col in visibleColumns"
        :key="col.key"
        type="button"
        class="rtab__h"
        :class="{ 'rtab__h--right': col.right, 'rtab__h--on': sort === col.key }"
        @click="$emit('sort', col.key)"
      >
        {{ col.label }}
        <span v-if="sort === col.key" class="rtab__arrow">{{ dir === "desc" ? "▼" : "▲" }}</span>
      </button>
      <div></div>
    </div>

    <!-- narrow: sort chips -->
    <div v-else class="rtab__chips">
      <span class="rtab__chipslbl">Orden</span>
      <button
        v-for="col in visibleColumns"
        :key="col.key"
        type="button"
        class="rtab__chip"
        :class="{ 'rtab__chip--on': sort === col.key }"
        @click="$emit('sort', col.key)"
      >
        {{ col.label }}
        <span v-if="sort === col.key">{{ dir === "desc" ? "▼" : "▲" }}</span>
      </button>
    </div>

    <!-- wide rows -->
    <div v-if="wide && rows.length">
      <div
        v-for="(r, i) in rows"
        :key="i"
        class="rtab__row"
        :style="{ gridTemplateColumns: grid }"
        @click="$emit('open', r.raw)"
      >
        <div
          v-for="col in visibleColumns"
          :key="col.key"
          class="rtab__cell"
          :class="{ 'rtab__cell--right': col.right }"
        >
          <span
            v-if="cell(r, col).badge"
            class="rtab__badge"
            :style="{ background: cell(r, col).badgeBg, color: cell(r, col).badgeFg }"
          >{{ cell(r, col).badge }}</span>
          <span
            v-if="cell(r, col).text"
            class="rtab__text"
            :style="{ fontWeight: cell(r, col).weight, color: cell(r, col).color }"
            :title="cell(r, col).text"
          >{{ cell(r, col).text }}</span>
          <span v-if="cell(r, col).sub" class="rtab__sub" :title="cell(r, col).sub">{{ cell(r, col).sub }}</span>
        </div>
        <div class="rtab__actions">
          <button
            v-for="a in actions"
            :key="a.key"
            type="button"
            class="rtab__act"
            :title="a.label"
            @click.stop="$emit('action', { action: a.key, row: r.raw })"
          >
            <hero-icon :icon="a.icon" :size="16" />
          </button>
        </div>
      </div>
    </div>

    <!-- narrow rows: stacked cards -->
    <div v-else-if="rows.length" class="rtab__cards">
      <div v-for="(r, i) in rows" :key="i" class="rtab__card">
        <div @click="$emit('open', r.raw)">
          <div class="rtab__cardtop">
            <span
              v-if="r.mobile.badge"
              class="rtab__badge"
              :style="{ background: r.mobile.badgeBg, color: r.mobile.badgeFg }"
            >{{ r.mobile.badge }}</span>
            <span class="rtab__cardtitle">{{ r.mobile.title }}</span>
          </div>
          <div class="rtab__cardsub">{{ r.mobile.sub }}</div>
          <div class="rtab__cardstats">
            <div v-for="(s, j) in r.mobile.stats" :key="j" class="rtab__cardstat">
              <span class="rtab__cardstatval" :style="{ color: s.color || '#1a1814' }">{{ s.value }}</span>
              <span class="rtab__cardstatlbl">{{ s.label }}</span>
            </div>
          </div>
        </div>
        <div class="rtab__cardacts">
          <button
            v-for="a in actions"
            :key="a.key"
            type="button"
            class="rtab__cardact"
            @click.stop="$emit('action', { action: a.key, row: r.raw })"
          >
            <hero-icon :icon="a.icon" :size="17" />
            {{ a.label }}
          </button>
        </div>
      </div>
    </div>

    <div v-if="!rows.length && !loading" class="rtab__empty">
      {{ search ? "Sin resultados para “" + search + "”" : "Sin datos en este período" }}
    </div>

    <div class="rtab__foot">
      <span v-if="loading" class="rtab__spinner"></span>
      <span class="rtab__foottext">{{ footer }}</span>
    </div>

    <div ref="sentinel" class="rtab__sentinel"></div>
  </div>
</template>

<script>
// Level 4 of the Reportes hierarchy: the work list. Sortable, searchable, with
// per-tab column visibility + order, and one row shape for wide screens (a CSS
// grid whose tracks come from the column config) and another for narrow ones
// (stacked cards, so nothing is hidden behind a horizontal scroll).
import HeroIcon from "@/components/shared/HeroIcon";
import SearchIcon from "heroicons/24/outline/magnifying-glass.svg";
import ColumnsIcon from "heroicons/24/outline/adjustments-horizontal.svg";
import BarsIcon from "heroicons/24/outline/bars-3.svg";
import CheckIcon from "heroicons/24/outline/check.svg";
import ArrowUpIcon from "heroicons/24/outline/arrow-up.svg";
import ArrowDownIcon from "heroicons/24/outline/arrow-down.svg";

const STORE_KEY = "sartor.reportes.columns";

export default {
  components: { HeroIcon },

  props: {
    tabKey: { type: String, required: true },
    title: { type: String, default: "" },
    columns: { type: Array, default: () => [] },
    rows: { type: Array, default: () => [] },
    actions: { type: Array, default: () => [] },
    totalCount: { type: Number, default: 0 },
    sort: { type: String, default: "" },
    dir: { type: String, default: "desc" },
    search: { type: String, default: "" },
    loading: { type: Boolean, default: false },
    hasMore: { type: Boolean, default: false },
    wide: { type: Boolean, default: true },
    defaultHidden: { type: Object, default: () => ({}) }
  },

  data: () => ({
    SearchIcon,
    ColumnsIcon,
    BarsIcon,
    CheckIcon,
    ArrowUpIcon,
    ArrowDownIcon,
    colsOpen: false,
    order: {}, // tabKey -> [colKey]
    hiddenMap: {}, // tabKey -> { colKey: true }
    observer: null
  }),

  computed: {
    hidden() {
      const stored = this.hiddenMap[this.tabKey];
      return stored === undefined ? this.defaultHidden : stored;
    },

    orderedColumns() {
      const saved = this.order[this.tabKey];
      if (!saved) return this.columns.slice();
      const byKey = {};
      this.columns.forEach(c => {
        byKey[c.key] = c;
      });
      // saved order first, then any column added since it was saved
      const listed = saved.map(k => byKey[k]).filter(Boolean);
      const extra = this.columns.filter(c => !saved.includes(c.key));
      return listed.concat(extra);
    },

    visibleColumns() {
      return this.orderedColumns.filter(c => !this.hidden[c.key]);
    },

    actionsWidth() {
      return 8 + this.actions.length * 36;
    },

    grid() {
      return this.visibleColumns.map(c => c.w).join(" ") + " " + this.actionsWidth + "px";
    },

    // the table can't usefully shrink below its columns' minimums — the page
    // scrolls horizontally instead of squeezing every cell into ellipsis
    tableMin() {
      return this.visibleColumns.reduce((sum, c) => sum + (c.min || 90), 0) + this.actionsWidth;
    },

    footer() {
      if (this.loading) return "Cargando…";
      if (this.hasMore) return "Deslizá para cargar más";
      return this.totalCount ? "Fin de la lista · " + this.totalCount + " en total" : "";
    }
  },

  created() {
    try {
      const stored = JSON.parse(window.localStorage.getItem(STORE_KEY) || "null");
      if (stored) {
        this.order = stored.order || {};
        this.hiddenMap = stored.hidden || {};
      }
    } catch (e) {
      /* private mode / corrupt value — fall back to the defaults */
    }
  },

  mounted() {
    this.observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && this.hasMore && !this.loading) this.$emit("load-more");
      },
      { rootMargin: "400px" }
    );
    if (this.$refs.sentinel) this.observer.observe(this.$refs.sentinel);
    document.addEventListener("mousedown", this.onDocDown, true);
  },

  beforeDestroy() {
    if (this.observer) this.observer.disconnect();
    document.removeEventListener("mousedown", this.onDocDown, true);
  },

  methods: {
    cell(row, col) {
      return row.cells[col.key] || {};
    },

    closeCols() {
      this.colsOpen = false;
    },

    // the popover is a plain absolutely-positioned div, so it closes itself on
    // any pointer-down outside its own box (and outside the button that opened it)
    onDocDown(e) {
      if (!this.colsOpen) return;
      const pop = this.$refs.colspop;
      if (pop && pop.contains(e.target)) return;
      if (e.target.closest && e.target.closest(".rtab__colsbtn")) return;
      this.colsOpen = false;
    },

    persist() {
      try {
        window.localStorage.setItem(STORE_KEY, JSON.stringify({ order: this.order, hidden: this.hiddenMap }));
      } catch (e) {
        /* private mode — the preference just won't survive the session */
      }
    },

    toggleCol(key) {
      const next = Object.assign({}, this.hidden);
      if (next[key]) delete next[key];
      else if (this.visibleColumns.length > 2) next[key] = true;
      else return this.$emit("notice", "Al menos dos columnas visibles");
      this.$set(this.hiddenMap, this.tabKey, next);
      this.persist();
      return null;
    },

    moveCol(key, delta) {
      const list = this.orderedColumns.map(c => c.key);
      const i = list.indexOf(key);
      const j = i + delta;
      if (i < 0 || j < 0 || j >= list.length) return;
      list.splice(j, 0, list.splice(i, 1)[0]);
      this.$set(this.order, this.tabKey, list);
      this.persist();
    },

    resetCols() {
      this.$set(this.order, this.tabKey, null);
      this.$set(this.hiddenMap, this.tabKey, {});
      this.persist();
    }
  }
};
</script>

<style scoped>
.rtab {
  background: #fff;
  border: 1px solid #ece7de;
  border-radius: 14px;
  box-shadow: 0 8px 24px -16px rgba(40, 33, 22, 0.18);
}

/* ---- header ---- */
.rtab__head {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  padding: 13px 16px;
  border-bottom: 1px solid #ece7de;
  border-radius: 14px 14px 0 0;
  position: relative;
}
.rtab__title { font-size: 13.5px; font-weight: 700; color: #1a1814; }
.rtab__count { font-size: 12px; color: #a39a8c; }
.rtab__tools { margin-left: auto; display: flex; align-items: center; gap: 8px; }

.rtab__colsbtn {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  background: #fff;
  color: #56504a;
  border: 1px solid #e3ddd2;
  border-radius: 8px;
  height: 34px;
  padding: 0 12px;
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
}
.rtab__colsbtn--on { background: #f4f0e8; color: #1a1814; border-color: #dcd4c6; }
.rtab__colscount { font-size: 11px; color: #a39a8c; font-weight: 600; }

.rtab__searchwrap { position: relative; display: flex; align-items: center; }
.rtab__searchicon { position: absolute; left: 9px; color: #a39a8c; pointer-events: none; }
.rtab__search {
  border: 1px solid #e3ddd2;
  border-radius: 8px;
  padding: 7px 11px 7px 30px;
  font-size: 12.5px;
  color: #1a1814;
  width: 190px;
  background: #fff;
  outline: none;
}
.rtab__search:focus { border-color: #cfc7b8; }

/* ---- columns popover ---- */
.rtab__colspop {
  position: absolute;
  top: 52px;
  right: 16px;
  z-index: 8;
  width: 286px;
  background: #fff;
  border: 1px solid #dcd4c6;
  border-radius: 12px;
  box-shadow: 0 18px 40px -20px rgba(40, 33, 22, 0.5);
  padding: 8px;
}
.rtab__colshead {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 8px 8px;
  font-size: 10.5px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #a39a8c;
  font-weight: 700;
}
.rtab__colsreset {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 11.5px;
  font-weight: 600;
  color: #b52118;
  padding: 0;
  text-transform: none;
  letter-spacing: normal;
}
.rtab__colrow { display: flex; align-items: center; gap: 6px; padding: 4px 6px; border-radius: 8px; }
.rtab__colrow:hover { background: #faf8f4; }
.rtab__colgrip { color: #cdc4b5; flex: none; }
.rtab__coltoggle {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 9px;
  background: none;
  border: none;
  cursor: pointer;
  text-align: left;
  padding: 4px 0;
}
.rtab__colbox {
  flex: none;
  width: 17px;
  height: 17px;
  border-radius: 5px;
  border: 1.5px solid #dcd4c6;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
}
.rtab__colbox--on { background: #b52118; border-color: #b52118; }
.rtab__collbl {
  font-size: 13px;
  font-weight: 600;
  color: #1a1814;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.rtab__collbl--off { font-weight: 400; color: #a39a8c; }
.rtab__colmove {
  flex: none;
  width: 26px;
  height: 26px;
  border: none;
  background: none;
  cursor: pointer;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #756f64;
}
.rtab__colmove:hover:not(:disabled) { background: #f1ece3; }
.rtab__colmove:disabled { color: #e3ddd2; cursor: default; }

/* ---- wide header + rows ---- */
.rtab__hrow {
  display: grid;
  background: #faf8f4;
  border-bottom: 1px solid #ece7de;
}
.rtab__h {
  display: flex;
  align-items: center;
  gap: 5px;
  background: transparent;
  border: none;
  cursor: pointer;
  height: 38px;
  padding: 0 12px;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  color: #a39a8c;
  white-space: nowrap;
  overflow: hidden;
}
.rtab__h--right { justify-content: flex-end; }
.rtab__h--on { color: #1a1814; }
.rtab__arrow { font-size: 9px; color: #b52118; }

.rtab__row {
  display: grid;
  border-bottom: 1px solid #f4f0e8;
  cursor: pointer;
  background: #fff;
}
.rtab__row:hover { background: #faf8f4; }
.rtab__cell {
  min-width: 0;
  min-height: 48px;
  padding: 7px 12px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
}
.rtab__cell--right { align-items: flex-end; }
.rtab__text {
  font-size: 13px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  font-variant-numeric: tabular-nums;
}
.rtab__sub {
  font-size: 11.5px;
  color: #a39a8c;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  margin-top: 2px;
}
.rtab__badge {
  font-size: 10px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 999px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  white-space: nowrap;
}
.rtab__actions { display: flex; align-items: center; justify-content: flex-end; gap: 4px; padding: 0 10px 0 0; }
.rtab__act {
  flex: none;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid #e3ddd2;
  background: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #56504a;
}
.rtab__act:hover { border-color: #dcd4c6; background: #f4f0e8; }

/* ---- narrow ---- */
.rtab__chips {
  display: flex;
  gap: 7px;
  overflow-x: auto;
  padding: 11px 14px;
  background: #faf8f4;
  border-bottom: 1px solid #ece7de;
}
.rtab__chipslbl {
  flex: none;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #a39a8c;
  align-self: center;
}
.rtab__chip {
  flex: none;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: 1px solid #e3ddd2;
  background: #fff;
  color: #56504a;
  border-radius: 999px;
  min-height: 44px;
  padding: 0 14px;
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
}
.rtab__chip--on { background: #1a1814; border-color: #1a1814; color: #fff; }

.rtab__cards { display: flex; flex-direction: column; }
.rtab__card { border-bottom: 1px solid #f4f0e8; padding: 13px 14px; cursor: pointer; }
.rtab__cardtop { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.rtab__cardtitle {
  font-size: 14.5px;
  font-weight: 600;
  color: #1a1814;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.rtab__cardsub {
  font-size: 12.5px;
  color: #756f64;
  margin-top: 3px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.rtab__cardstats { display: flex; gap: 16px; margin-top: 9px; flex-wrap: wrap; }
.rtab__cardstat { display: flex; flex-direction: column; }
.rtab__cardstatval { font-size: 15px; font-weight: 700; line-height: 1; font-variant-numeric: tabular-nums; }
.rtab__cardstatlbl { font-size: 10.5px; color: #a39a8c; margin-top: 3px; }
.rtab__cardacts { display: flex; gap: 8px; margin-top: 12px; }
.rtab__cardact {
  flex: 1 1 0;
  min-height: 44px;
  border-radius: 10px;
  border: 1px solid #e3ddd2;
  background: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  font-size: 13px;
  font-weight: 600;
  color: #56504a;
}

/* ---- empty + footer ---- */
.rtab__empty {
  margin: 18px;
  border: 1px dashed #dcd4c6;
  border-radius: 12px;
  padding: 36px;
  text-align: center;
  color: #a39a8c;
  font-size: 14px;
}
.rtab__foot {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  height: 56px;
  background: #faf8f4;
  border-top: 1px solid #ece7de;
  border-radius: 0 0 14px 14px;
}
.rtab__foottext { font-size: 12px; font-weight: 600; color: #a39a8c; }
.rtab__spinner {
  display: block;
  width: 15px;
  height: 15px;
  border-radius: 50%;
  border: 2px solid #e3ddd2;
  border-top-color: #b52118;
  animation: rtab-spin 0.7s linear infinite;
}
@keyframes rtab-spin {
  to { transform: rotate(360deg); }
}
.rtab__sentinel { height: 1px; }

@media (max-width: 1199px) {
  .rtab__tools { width: 100%; margin-left: 0; }
  .rtab__searchwrap { flex: 1 1 auto; }
  .rtab__search { width: 100%; min-height: 44px; }
}
</style>
